import { useEffect, useState } from 'react';

import {
  Box,
  Divider,
  Link,
  Stack,
  TextField,
  Typography,
  Button as MuiButton,
} from '@mui/material';

import { Button } from '@karehero/llama/components/atoms/Button/Button';
import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';

import HomeIndicator from '@/designs-dev/device/HomeIndicator';

// View-only representation of how Kinde auth actually surfaces on mobile: a
// system in-app browser SHEET that slides up over the app (ASWebAuthenticationSession
// / Custom Tab) showing Kinde's hosted — brandable — page. The OS owns the chrome
// (lock + domain + Cancel); the layout below is Kinde's, not ours.
//
// Step copy replicates the real staging hosted pages (fetched from
// auth-staging.karehero.com, including walking the sign-in form to the
// "Check your inbox" code step): a provider or email, an emailed one-time code,
// and — on registration — a set-password step. The set-password page is the one
// step that couldn't be fetched without completing a real registration, so its
// copy is best-effort.
//
// The headings drop staging's "(Staging)" suffix. It is genuinely what staging
// shows, but this page is here to be designed and read, and an environment name
// in the title is neither.
//
// 'reset' is the forgot-password page, and it is here for the same reason the
// other two are: main has no password screen of its own. Auth is `login`,
// `register` and `logout` from the Kinde SDK (front-end/pwa/src/hooks/useAuth.ts),
// so a password is set, reset and checked on Kinde's pages, and the flow we can
// design is what surrounds them. Its copy is best-effort in the same way the
// set-password step is: reaching it needs a real account to reset.

type Variant = 'sign-in' | 'register' | 'reset';

// Kinde masks the address on the code step: v•••••@k•••••.com
const maskEmail = (raw: string) => {
  const [local = '', domain = ''] = raw.split('@');
  if (!local || !domain) return 'y•••••@e•••••.com';
  const tld = domain.includes('.') ? domain.split('.').pop() : 'com';
  return `${local[0]}•••••@${domain[0]}•••••.${tld}`;
};

const KindeHostedSheet = ({
  onClose,
  onComplete,
  variant = 'sign-in',
  initialStep = 'form',
  error,
}: {
  onClose: () => void;
  onComplete?: () => void;
  /** Which hosted Kinde page the sheet opens on: sign-in, registration or reset. */
  variant?: Variant;
  /** Jump-state for handover minis: open on a specific hosted step. */
  initialStep?: 'form' | 'code' | 'password';
  /**
   * What Kinde said went wrong, under the field on the open step. Kinde's own
   * copy, so a state that shows one is quoting a page we do not own.
   */
  error?: string;
}) => {
  const [open, setOpen] = useState(false);
  // "No account? Create one" swaps page in place, like the real hosted flow.
  const [page, setPage] = useState<Variant>(variant);
  const [step, setStep] = useState<'form' | 'code' | 'password'>(initialStep);
  const [email, setEmail] = useState('');
  useEffect(() => setOpen(true), []);

  const close = () => {
    setOpen(false);
    window.setTimeout(onClose, 260);
  };

  const isRegister = page === 'register';
  const isReset = page === 'reset';

  // Under the field on whichever step is open. Kinde reports inline, not in a
  // banner, so this reads as the field's own error rather than the page's.
  const errorLine = error ? (
    <Typography variant='caption' sx={{ color: 'error.main' }}>
      {error}
    </Typography>
  ) : null;

  const fieldProps = {
    fullWidth: true,
    slotProps: { inputLabel: { shrink: true } },
  } as const;

  // The three providers as pill chips carrying only their mark, the way the
  // screen on main draws them. A row of marks reads as a choice between equals;
  // one full-width "Continue with Google" made Google the way in and left
  // nowhere for Apple and Facebook to go.
  const socialButtons = (
    <Stack sx={{ justifyContent: 'center' }} direction='row' spacing={2}>
      {[
        { id: 'google', label: 'Continue with Google' },
        { id: 'apple', label: 'Continue with Apple' },
        { id: 'facebook', label: 'Continue with Facebook' },
      ].map((p) => (
        <MuiButton
          key={p.id}
          size='large'
          onClick={onComplete}
          aria-label={p.label}
          sx={{
            flex: 1,
            minWidth: 0,
            py: 1.5,
            borderRadius: '999px',
            bgcolor: 'background.paper',
            '&:hover': { bgcolor: 'background.paper' },
          }}
        >
          <Box
            sx={{ width: 22, height: 22 }}
            component='img'
            src={`/vector/${p.id}.svg`}
            alt=''
          />
        </MuiButton>
      ))}
    </Stack>
  );

  const orDivider = (
    <Divider
      sx={{
        '&::before, &::after': { borderColor: 'custom.neutral.400' },
      }}
    >
      <Typography variant='body2' color='text.secondary'>
        Or
      </Typography>
    </Divider>
  );

  return (
    <>
      {/* dimmed app behind */}
      <Box
        onClick={close}
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          bgcolor: 'common.black',
          opacity: open ? 0.4 : 0,
          transition: 'opacity 260ms ease',
        }}
      />

      {/* the sheet */}
      <Box
        sx={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 0,
          height: '92%',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'background.default',
          overflow: 'hidden',
          borderTopLeftRadius: '14px',
          borderTopRightRadius: '14px',
          transform: open ? 'translateY(0)' : 'translateY(100%)',
          transition: 'transform 280ms ease',
        }}
      >
        {/* OS-drawn browser chrome — not ours to restyle */}
        <Stack
          sx={{
            alignItems: 'center',
            px: 1.5,
            py: 1.25,
            flexShrink: 0,
            bgcolor: 'custom.neutral.100',
            borderBottom: 1,
            borderColor: 'custom.neutral.200',
          }}
          direction='row'
        >
          <Box
            component='button'
            onClick={close}
            aria-label='Cancel'
            sx={{
              border: 0,
              bgcolor: 'transparent',
              p: 0,
              cursor: 'pointer',
              minWidth: 56,
              textAlign: 'left',
            }}
          >
            <Typography variant='body2' color='primary.main'>
              Cancel
            </Typography>
          </Box>
          <Stack
            sx={{
              alignItems: 'center',
              flex: 1,
              justifyContent: 'center',
              minWidth: 0,
            }}
            direction='row'
            spacing={0.5}
          >
            {/* shield-halved, not lock: llama's icon set registers 172
                icons and faLock is not among them, so `icon='lock'`
                typechecked and then rendered nothing. */}
            <Icon icon='shield-halved' fontSize={11} />
            <Typography variant='caption' color='text.secondary' noWrap>
              auth-staging.karehero.com
            </Typography>
          </Stack>
          <Box sx={{ minWidth: 56 }} />
        </Stack>

        {/* Kinde-hosted (branded) content */}
        <Box sx={{ flex: 1, overflow: 'auto', px: 3, pt: 7 }}>
          <Stack sx={{ alignItems: 'center', mb: 4 }} spacing={1}>
            <Box
              sx={{ width: 42, height: 'auto' }}
              component='img'
              src='/vector/karehero-logo.png'
              alt='KareHero'
            />
            {/* Marine green, not the heading colour. Loft's palette binds
                text/title to charcoal and primary.main to marine green/700,
                which re-indexes to #002825 — near-black either way. The brand
                green is 500, so it is named. */}
            <Typography
              sx={{ textAlign: 'center', color: 'custom.marineGreen.500' }}
              variant='h2'
            >
              {step === 'code'
                ? 'Check your inbox'
                : step === 'password'
                  ? 'Set your password'
                  : isRegister
                    ? 'Create account'
                    : isReset
                      ? 'Reset your password'
                      : 'Welcome back'}
            </Typography>
          </Stack>

          {step === 'code' ? (
            <Stack spacing={2}>
              <Typography variant='body2' color='text.secondary'>
                Enter the code we just sent to {maskEmail(email)}
              </Typography>
              <TextField
                label='Code'
                inputMode='numeric'
                autoComplete='one-time-code'
                {...fieldProps}
              />
              {errorLine}
              <Button
                variant='primary'
                size='lg'
                isFullWidth
                ariaLabel='Continue'
                onPress={() =>
                  isRegister || isReset ? setStep('password') : onComplete?.()
                }
              >
                Continue
              </Button>
              <Stack sx={{ alignItems: 'center' }} spacing={0.5}>
                <Typography variant='body2' color='text.secondary'>
                  Didn&apos;t receive a code?
                </Typography>
                <Link component='button' type='button' variant='body2'>
                  Resend code
                </Link>
              </Stack>
            </Stack>
          ) : step === 'password' ? (
            <Stack spacing={2}>
              <TextField label='Password' type='password' {...fieldProps} />
              <TextField
                label='Confirm password'
                type='password'
                {...fieldProps}
              />
              <Typography variant='caption' color='text.secondary'>
                Make sure it&apos;s at least 8 characters.
              </Typography>
              {errorLine}
              <Button
                variant='primary'
                size='lg'
                isFullWidth
                ariaLabel='Continue'
                onPress={onComplete}
              >
                Continue
              </Button>
            </Stack>
          ) : (
            <Stack spacing={2}>
              {isReset ? (
                <Typography variant='body2' color='text.secondary'>
                  Enter your email address and we&apos;ll send you a code.
                </Typography>
              ) : (
                <>
                  {socialButtons}
                  {orDivider}
                </>
              )}
              {/* One field. Kinde takes first and last name as optional, and
                  nothing in registration fails without them — a missing name is
                  a warning and an empty string, not an error. The name is asked
                  for in onboarding instead, where there is a reason to ask. */}
              <TextField
                label='Email address'
                type='email'
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                {...fieldProps}
              />
              {errorLine}
              <Button
                variant='primary'
                size='lg'
                isFullWidth
                ariaLabel={isRegister ? 'Create your account' : 'Continue'}
                onPress={() => setStep('code')}
              >
                {isRegister ? 'Create your account' : 'Continue'}
              </Button>
              {!isRegister && !isReset && (
                <Typography
                  sx={{ textAlign: 'center' }}
                  variant='body2'
                  color='text.secondary'
                >
                  No account?{' '}
                  <Link
                    component='button'
                    type='button'
                    variant='body2'
                    onClick={() => setPage('register')}
                    sx={{ verticalAlign: 'baseline' }}
                  >
                    Create one
                  </Link>
                </Typography>
              )}
            </Stack>
          )}
        </Box>

        {/* footer copy differs slightly between the two hosted pages */}
        <Box sx={{ px: 3, pb: 1, textAlign: 'center' }}>
          <Typography variant='caption' color='text.secondary'>
            {isRegister ? (
              <>
                By continuing, you agree to our policies{' '}
                <Link variant='caption'>Terms of use</Link> ·{' '}
                <Link variant='caption'>Privacy Policy</Link>
              </>
            ) : (
              'By continuing, you agree to our terms of use and privacy policies'
            )}
          </Typography>
        </Box>
        <HomeIndicator />
      </Box>
    </>
  );
};

export default KindeHostedSheet;
